import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FiDownload } from "react-icons/fi";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../../hooks/useColumnVisibility";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../components/BaseUI/Table";
import Button from "../../../../components/BaseUI/Button";
import Lozenge from "../../../../components/BaseUI/Lozenge";
import Pagination from "../../../../components/BaseUI/Pagination";
import {
  SectionSpinner,
  SectionEmpty,
  fmtMins,
  toISO,
  exportXLSX,
} from "../shared/utils";
import { API_URL } from "../shared/constants";
import DowntimeReportShell from "./DowntimeReportShell";
import { useOEEReport } from "../OEE/useOEEReport";

const DOWNTIME_REASON_COLUMNS = [
  "Duruş Nedeni",
  "OEE Grubu",
  "OEE Sınıfı",
  "Süre",
  "Olay",
  "Pay %",
].map((name, id) => ({ name, id, sortable: false }));

const DowntimeReasonsReport = () => {
  useTitle("Rovion MES - Duruş Sebep Raporu");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
    totalStopMinutes: 0,
  });

  const {
    filters,
    machines,
    shifts,
    teepData,
    loading,
    fetchData,
    toasts,
    dismissToast,
    authH,
  } = useOEEReport({
    autoFetch: false,
    stopReasonsPage: pagination.page,
    stopReasonsPageSize: pagination.pageSize,
  });

  const {
    visibleColumns,
    toggleColumnVisibility,
    resetToDefault,
  } = useColumnVisibility("downtime-reasons-list", DOWNTIME_REASON_COLUMNS);

  const reasons = teepData?.topReasons || [];
  const reasonsMeta = teepData?.topReasonsPagination;

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!reasonsMeta) return;
    setPagination((prev) => ({
      ...prev,
      page: reasonsMeta.page ?? prev.page,
      pageSize: reasonsMeta.pageSize ?? prev.pageSize,
      total: reasonsMeta.total ?? 0,
      totalPages: reasonsMeta.totalPages ?? 0,
      totalStopMinutes: reasonsMeta.totalStopMinutes ?? 0,
    }));
  }, [reasonsMeta]);

  const handleApply = useCallback(() => {
    if (pagination.page === 1) {
      fetchData({ stopReasonsPage: 1 });
    } else {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [fetchData, pagination.page]);

  const handlePageChange = useCallback(({ pagination: pg }) => {
    setPagination((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const exportReasons = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.machineIds.length)
        p.set("machineIds", filters.machineIds.join(","));
      if (filters.shiftId) p.set("shiftId", filters.shiftId);
      p.set("stopReasonsPage", "1");
      p.set("stopReasonsPageSize", "500");

      const r = await axios.get(`${API_URL}/api/teep?${p}`, authH());
      const allReasons = r.data.data?.topReasons || [];
      const totalMins =
        r.data.data?.topReasonsPagination?.totalStopMinutes ||
        allReasons.reduce((s, row) => s + (row.minutes || 0), 0);

      exportXLSX(
        allReasons.map((row) => ({
          "Duruş Nedeni": row.name,
          "OEE Grubu": row.groupName,
          "OEE Sınıfı": row.className,
          "Süre (dk)": row.minutes,
          "Olay Sayısı": row.count,
          "Pay %":
            totalMins > 0
              ? Math.round((row.minutes / totalMins) * 1000) / 10
              : 0,
        })),
        `durus-nedenleri-${filters.startDate}-${filters.endDate}`,
        "Duruş Sebep Raporu",
      );
    } catch {
      exportXLSX(
        reasons.map((row) => ({
          "Duruş Nedeni": row.name,
          "OEE Grubu": row.groupName,
          "OEE Sınıfı": row.className,
          "Süre (dk)": row.minutes,
          "Olay Sayısı": row.count,
        })),
        `durus-nedenleri-${filters.startDate}-${filters.endDate}`,
        "Duruş Sebep Raporu",
      );
    }
  }, [filters, authH, reasons]);

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!teepData || !reasons.length)
      return <SectionEmpty msg="Duruş nedeni verisi bulunamadı." />;

    const totalMins =
      pagination.totalStopMinutes ||
      reasons.reduce((s, r) => s + (r.minutes || 0), 0);

    return (
      <CardContainer
        title="Duruş Sebep Raporu Dökümü"
        actions={
          <Button
            appearance="subtle"
            onClick={exportReasons}
            iconBefore={<FiDownload size={13} />}
          >
            Excel İndir
          </Button>
        }
      >
        <Table
          columns={DOWNTIME_REASON_COLUMNS}
          visibleColumns={visibleColumns}
          search={false}
          hideFooter
        >
          {reasons.map((r, i) => {
            const share =
              totalMins > 0
                ? Math.round((r.minutes / totalMins) * 1000) / 10
                : 0;
            return (
              <Tr key={r.name + i} data={r}>
                {visibleColumns.includes(0) && (
                  <Td>
                    <strong>{r.name}</strong>
                  </Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td>
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: r.groupColor || "#94a3b8",
                        marginRight: 6,
                      }}
                    />
                    {r.groupName}
                  </Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    <Lozenge appearance="default-subtle" isBold>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: r.classColor || "#94a3b8",
                          marginRight: 4,
                        }}
                      />
                      {r.className}
                    </Lozenge>
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>
                    <span className="rp-ref-rejected">{fmtMins(r.minutes)}</span>
                  </Td>
                )}
                {visibleColumns.includes(4) && <Td>{r.count}</Td>}
                {visibleColumns.includes(5) && <Td>{share}%</Td>}
              </Tr>
            );
          })}
        </Table>
        <Pagination
          totalItems={pagination.total}
          totalPerPage={pagination.pageSize}
          currentPage={pagination.page}
          onPageChange={handlePageChange}
        />
      </CardContainer>
    );
  };

  return (
    <DowntimeReportShell
      title="Duruş Sebep Raporu"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={handleApply}
      toasts={toasts}
      dismissToast={dismissToast}
      columns={DOWNTIME_REASON_COLUMNS}
      visibleColumns={visibleColumns}
      onToggleColumn={toggleColumnVisibility}
      onResetColumns={resetToDefault}
    >
      {content()}
    </DowntimeReportShell>
  );
};

export default withAuth(DowntimeReasonsReport);
